import moment from "moment";

import { NO_DUE_DATE, OVERDUE, TODAY } from "~/actionItems/constants";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import type { GroupType } from "~/actionItems/types";
import type { NodesFromEdges } from "~/lib/utils";
import type { UserAttrs } from "~/ui/legacy/Avatar";

import type { AssigneeCompletionData } from "./ActionItemCompletionMenu";

import type { ActionItemGroupsListQuery$data } from "~/__generated__/ActionItemGroupsListQuery.graphql";

export function isNotNull<T>(item: T | undefined | null): item is NonNullable<T> {
	return item != null;
}

export function getKeyForDueDate(dueDate: string | null, today: string): string {
	if (!dueDate) {
		return NO_DUE_DATE;
	}
	if (dueDate < today) {
		return OVERDUE;
	}
	if (dueDate === today) {
		return TODAY;
	}
	return moment(dueDate).format("dddd, MMM D");
}

export function generateSyntheticGroup(name: string, id?: string, assignee?: UserAttrs): GroupType {
	return {
		id: id || name,
		name: name,
		order: 0,
		isDefaultGroup: false,
		items: {
			edges: [],
		},
		assignee,
	};
}

type ActionItemGroup = NodesFromEdges<ActionItemGroupsListQuery$data["actionItemGroups"]>;
type ActionItemNode = ActionItemGroup["items"]["edges"][0];

// For our action items orders we want biggest orders to be at the top
// And smallest ones at the bottom, essentially a descending order
function invertedASCIISorting(a: string, b: string) {
	for (let i = 0; i < a.length; i++) {
		if (i >= b.length) {
			return -1;
		}
		const asciiCodeForA = a[i].charCodeAt(0);
		const asciiCodeForB = b[i].charCodeAt(0);
		if (asciiCodeForA > asciiCodeForB) {
			return -1;
		} else if (asciiCodeForA < asciiCodeForB) {
			return 1;
		}
	}
	return 1;
}

export function customWithGroupSort(a: ActionItemNode, b: ActionItemNode) {
	return invertedASCIISorting(a?.node.userOrderWithinGroup || "", b?.node.userOrderWithinGroup || "");
}

export function customSort(a: ActionItemNode, b: ActionItemNode) {
	return invertedASCIISorting(a?.node.userOrder || "", b?.node.userOrder || "");
}

export function dueDateSort(a: ActionItemNode, b: ActionItemNode) {
	// We use "z" here so non existent due dates go to the bottom
	if ((a?.node.dueDate || "z") > (b?.node.dueDate || "z")) return 1;
	if ((a?.node.dueDate || "z") < (b?.node.dueDate || "z")) return -1;
	return createdAtSort(a, b);
}

export function createdAtSort(a: ActionItemNode, b: ActionItemNode) {
	if ((a?.node.createdAt || "") < (b?.node.createdAt || "")) return 1;
	return -1;
}

export function recordingOffsetSort(a: ActionItemNode, b: ActionItemNode) {
	const offsetA = a?.node?.recordingOffset ?? 0;
	const offsetB = b?.node?.recordingOffset ?? 0;
	return offsetA - offsetB;
}

export function encodeNoteUrl({
	noteId,
	blockKey,
	privateNote,
}: {
	noteId: string;
	blockKey?: string;
	privateNote?: boolean;
}): string {
	let encodedUrl = noteId;
	if (blockKey) encodedUrl += `/${blockKey}`;
	if (privateNote) encodedUrl += "/private";
	return encodedUrl;
}

export function nodeAttrsToStatus(attrs: { [key: string]: unknown } | null): NoteBlockStatus {
	if (!attrs) return NoteBlockStatus.NOT_DONE;
	if (attrs.done) return NoteBlockStatus.DONE;
	if (attrs.wont_do) return NoteBlockStatus.WONT_DO;
	return NoteBlockStatus.NOT_DONE;
}

export function toggleNoteBlockStatus(status: NoteBlockStatus | null): NoteBlockStatus {
	if (!status || status === NoteBlockStatus.NOT_DONE) return NoteBlockStatus.DONE;
	return NoteBlockStatus.NOT_DONE;
}

export function assigneeBasedDoneAndWontDo(
	assignees: Array<AssigneeCompletionData>,
	statusCondition: StatusCondition,
	currentDone: boolean,
	currentWontDo: boolean,
) {
	if (assignees.length <= 1) {
		return { done: currentDone, wontDo: currentWontDo };
	}

	let amountOfDone = 0;
	let amountOfWontDo = 0;
	assignees.forEach(assignee => {
		if (assignee.status === NoteBlockStatus.DONE) {
			amountOfDone++;
		} else if (assignee.status === NoteBlockStatus.WONT_DO) {
			amountOfWontDo++;
		}
	});

	// For SINGLE, any amount of either status will change the entire block's status
	if (statusCondition === StatusCondition.SINGLE) {
		return {
			done: amountOfDone === assignees.length,
			wontDo: amountOfWontDo === assignees.length,
		};
	}

	// Otherwise, all assignees must have each status in order to change it for the whole block
	return {
		done: amountOfDone === assignees.length,
		wontDo: amountOfWontDo === assignees.length,
	};
}
